import { useRef, useEffect, useState, useCallback } from "react"
import { cn } from "@/lib/utils"
import { ConciergeInsightCard } from "./ConciergeInsightCard"
import { ConciergeQuickActions } from "./ConciergeQuickActions"
import type { ConciergeMessage } from "./types"

interface ConciergeMessagesProps {
  messages: ConciergeMessage[]
  isLoading: boolean
  onQuickAction: (action: string) => void
}

function formatRelativeTime(date: Date): string {
  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHr = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHr / 24)

  if (diffSec < 60) return "Just now"
  if (diffMin < 60) return `${diffMin} min ago`
  if (diffHr < 24) return `${diffHr} hr ago`
  if (diffDay === 1) return "Yesterday"
  return date.toLocaleDateString()
}

function TypingIndicator() {
  return (
    <div className="self-start max-w-[92%]">
      <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-[typing-bounce_1.4s_infinite_ease-in-out]" />
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-[typing-bounce_1.4s_0.2s_infinite_ease-in-out]" />
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-[typing-bounce_1.4s_0.4s_infinite_ease-in-out]" />
      </div>
    </div>
  )
}

export function ConciergeMessages({ messages, isLoading, onQuickAction }: ConciergeMessagesProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [, setTick] = useState(0)

  // Auto-scroll to bottom on new messages
  const isNearBottom = useCallback(() => {
    const el = containerRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }, [])

  useEffect(() => {
    if (isNearBottom()) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages, isLoading, isNearBottom])

  // Refresh timestamps every 30s
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30_000)
    return () => clearInterval(interval)
  }, [])

  // Find the last assistant message index (for showing quick actions only on last)
  let lastAssistantIdx = -1
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant" && messages[i].content) {
      lastAssistantIdx = i
      break
    }
  }

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
      {messages.map((msg, idx) => {
        const isLastAssistant = idx === lastAssistantIdx

        return (
          <div
            key={msg.id}
            className={cn(
              "group max-w-[80%] flex flex-col gap-0.5",
              msg.role === "assistant" ? "self-start" : "self-end",
            )}
          >
            {/* Message bubble */}
            {msg.content && (
              <div
                className={cn(
                  "px-4 py-3 rounded-2xl text-[13.5px] leading-[1.55] whitespace-pre-wrap",
                  msg.role === "assistant"
                    ? "bg-muted rounded-bl-md text-foreground"
                    : "bg-primary text-primary-foreground rounded-br-md",
                )}
              >
                {msg.content}
              </div>
            )}

            {/* Insight card (inline after assistant message) */}
            {msg.insightCard && <ConciergeInsightCard card={msg.insightCard} />}

            {/* Quick actions — only on the LAST assistant message */}
            {isLastAssistant && msg.quickActions && msg.quickActions.length > 0 && (
              <ConciergeQuickActions actions={msg.quickActions} onSelect={onQuickAction} />
            )}

            {/* Timestamp — visible on hover */}
            {msg.content && (
              <span
                className={cn(
                  "text-[10px] text-muted-foreground/50 px-1 opacity-0 group-hover:opacity-100 transition-opacity",
                  msg.role === "user" && "text-right",
                )}
              >
                {formatRelativeTime(msg.timestamp)}
              </span>
            )}
          </div>
        )
      })}

      {isLoading && <TypingIndicator />}

      <div ref={bottomRef} />
    </div>
  )
}
